import get from 'lodash/get';
import uniq from 'lodash/uniq';
import union from 'lodash/union';
import filter from 'lodash/filter';
import reject from 'lodash/reject';
import isEmpty from 'lodash/isEmpty';
import without from 'lodash/without';
import includes from 'lodash/includes';
import difference from 'lodash/difference';

import {
  ADD_SECTION,
  SET_IS_HEADER_EDITOR_OPEN,
  SET_IS_THEME_SELECTOR_OPEN,
  SET_IS_STYLE_SELECTOR_OPEN,
  SET_EDITING_TILE_CAPTION,
  SET_IS_PAGE_SCOPED_CSS_EDITOR_OPEN,
  SET_IS_TRANSLATIONS_EDITOR_OPEN,
  QUEUE_OVERLAY_CONTENT_ITEM_FOR_DELETE,
  QUEUE_DOWNLOADABLE_CONTENT_ITEM_FOR_DELETE,
  SET_SHOULD_SHOW_GRID_LINES,
  SET_IS_EDITING_TILE_TAXONOMY,
  SET_CAN_PASTE_TILE_DATA,
  SET_CAN_PASTE_SECTION_DATA,
  SET_CAN_PASTE_SUBPAGE_DATA,
  SET_CLIPBOARD_ITEMS_CACHE,
  SET_TEMPORARY_PAGE_TITLE,
  REMOVE_CLIPBOARD_ITEMS_FROM_CACHE_BY_ID,
  SET_SIDEBAR_EXPANSION_SIZE,
  END_ALL_EDITING,
  UPDATE_SIDEBAR_OPENED_COMPONENT_CACHE,
  SET_SECTION_EDITOR_CURRENT_TAB,
} from 'zoomforth_codesbane_editor/action_types.js';

import {
  CLIPBOARD_ITEM_TYPE_TILE,
  CLIPBOARD_ITEM_TYPE_SECTION,
  CLIPBOARD_ITEM_TYPE_SUBPAGE
} from 'zoomforth_codesbane_editor/lib/user_clipboard/const.js';

import {
  TILE_EDITOR_TAB_STYLING,
  SECTION_EDITOR_TAB_STYLING
} from 'zoomforth_codesbane_editor/const/const.js';

import { get as getUserStore } from 'zoomforth_zf/appglobals/user.js';

import { getIdFrom } from 'zoomforth_codesbane_viewer/lib/id_utils.js';

export default (state = {}, action)=> {

  switch (action.type) {

  case 'SHOW_DEBUG_BAR': 
    return {
      ...state,
      debugBar: true
    };

  case 'HIDE_DEBUG_BAR':
    return {
      ...state,
      debugBar: false
    };

  case 'SET_CURRENT_FROALA_LINK_EDITOR_INSTANCE':
    return {
      ...state,
      currentFroalaLinkEditorInstance: action.editorInstance
    };

  case 'SET_IS_PAGE_SCOPED_CSS_VALID':
    return {
      ...state,
      isPageScopedCssValid: action.isValid
    };

  case 'BEGIN_CHOOSE_TARGET_FOR_INTERNAL_LINK':
    return {
      ...state,
      isChoosingTarget: true,
      chosenSectionId: action.sectionId,
      chosenContentUuid: action.contentUuid
    };

  case 'SET_CHOSEN_SECTION':
    return {
      ...state,
      chosenSectionId: action.sectionId,
      // Clear any previously chosen content item when choosing a different section
      chosenContentUuid: null
    };

  case 'SET_CHOSEN_CONTENT_ITEM':
    return {
      ...state,
      chosenContentUuid: action.contentUuid,
      // Update the section to match the content item's section
      chosenSectionId: action.sectionId
    };

  case 'END_CHOOSE_TARGET_FOR_INTERNAL_LINK':
    return {
      ...state,
      chosenSectionId: null,
      chosenContentUuid: null,
      isChoosingTarget: false
    };

  case 'EDIT_SECTION':
    return {
      ...state,
      currentlyEditingSectionId: action.section ? action.section.id : null,
      currentlyEditingContentUuids: null
    };

  case 'SET_TEMPORARY_MENU_DATA': {
    return {
      ...state,
      temporaryMenuData: action.menuItem ?
        {
          menuItem: action.menuItem,
          subpage: action.subpage
        }
          :
        null
    };
  }

  case 'SET_TEMPORARY_FROALA_LINK_DATA': {
    return {
      ...state,
      temporaryFroalaLinkData: action.temporaryFroalaLinkData ?
        { ...action.temporaryFroalaLinkData }
      :
        null
    };
  }

  case 'SET_HAS_PASTED_AT_LEAST_ONCE_IN_FROALA': {
    return {
      ...state,
      hasPastedAtLeastOnceInFroala: true
    };
  }

  case 'SET_HAS_HIDDEN_FROALA_PASTE_NOTIFICATION': {
    return {
      ...state,
      hasHiddenFroalaPasteNotification: true
    };
  }

  case 'SET_TEMPORARY_TILE_ACTION_DATA': {
    return {
      ...state,
      temporaryTileActionData: action.tileActionData
    };
  }

  case 'SET_EDITING_OVERLAY_DATA': {
    return {
      ...state,
      editingOverlayData: action.editingOverlayData
    };
  }

  case 'REMOVE_SECTIONS': {
    // Be sure to stop editing a section if it was the one that was just deleted!
    return {
      ...state,
      currentlyEditingSectionId: 
        includes(action.sectionIds, state.currentlyEditingSectionId) ?
          null
        : 
          state.currentlyEditingSectionId
    };
  }

  case 'DELETE_CONTENT_ITEMS': {
    return {
      ...state,
      currentlyEditingContentUuids: without.apply(null, state.currentlyEditingContentUuids, action.contentUuids)
    };
  }

  case 'OPEN_PAGE_TITLE_EDITOR': {
    return {
      ...state,
      isOpenPageTitleEditor: true
    };
  }

  case 'CLOSE_PAGE_TITLE_EDITOR': {
    return {
      ...state,
      isOpenPageTitleEditor: false
    };
  }

  case 'SET_SECTION_AND_OVERRIDES': {
    return {
      ...state,
      sectionOverridesBySectionId: {
        ...state.sectionOverridesBySectionId,
        [action.sectionId]: {
          ...state.sectionOverridesBySectionId[action.sectionId],
          ...action.sectionOverrides
        }
      }
    };
  }

  case SET_CAN_PASTE_TILE_DATA: {
    return {
      ...state,
      canPasteTileData: action.canPaste
    };
  }

  case SET_CAN_PASTE_SECTION_DATA: {
    return {
      ...state,
      canPasteSectionData: action.canPaste
    };
  }

  case SET_CAN_PASTE_SUBPAGE_DATA: {
    return {
      ...state,
      canPasteSubpageData: action.canPaste
    };
  }

  case SET_CLIPBOARD_ITEMS_CACHE: {
    const { clipboardItems } = action;
    return {
      ...state,
      clipboardItems
    };
  }

  case REMOVE_CLIPBOARD_ITEMS_FROM_CACHE_BY_ID: {
    const { clipboardItems } = state;
    const { deletedClipboardItemsIds } = action;

    return {
      ...state,
      clipboardItems: reject(clipboardItems, item => includes(deletedClipboardItemsIds, item.id))
    };
  }

  case 'SET_TILE_IS_LOADING': {
    return {
      ...state,
      sectionOverridesBySectionId: {
        ...state.sectionOverridesBySectionId,
        [action.sectionId]: {
          ...state.sectionOverridesBySectionId[action.sectionId],
          editorOnlyTileDataByEid: {
            ...state.sectionOverridesBySectionId[action.sectionId].editorOnlyTileDataByEid,
            [action.eid]: { isLoading:action.value }
          }
        }
      }
    };
  }

  case 'SET_GRID_SECTION_SINGLE_TILE_EDITOR_CURRENT_TAB':
    return {
      ...state,
      gridSectionSingleTileEditorCurrentTab: action.currentTab
    };

  case 'SET_ACTIVE_TAB_ID':
    return {
      ...state,
      activeTabIdBySectionId: {
        ...state.activeTabIdBySectionId,
        [action.sectionId]: action.tabId
      },
      currentlyEditingContentUuids: union(action.contentUuids, state.currentlyEditingContentUuids),
    };

  case SET_SECTION_EDITOR_CURRENT_TAB:
    return {
      ...state,
      sectionEditorCurrentTab: action.currentTab
    };

  case SET_TEMPORARY_PAGE_TITLE: {
    return {
      ...state,
      temporaryPageTitle: action.pageTitle
    };
  }

  case 'SET_EDITING_CONTENT':
    return {
      ...state,
      temporaryTileActionData: null,
      editingOverlayData: null,
      currentlyEditingContentUuids: action.contentUuids,
      currentlyEditingSection: action.section,
    };

  case 'BEGIN_EDITING_CONTENT':
    return {
      ...state,
      currentlyEditingContentUuids: union(
        state.currentlyEditingContentUuids, 
        action.contentUuids
      )
    };

  case 'END_EDITING_CONTENT':
    return {
      ...state,
      currentlyEditingContentUuids: difference(
        state.currentlyEditingContentUuids,
        action.contentUuids
      ),
      currentlyEditingTileCaptionUuid: null
    };

  case UPDATE_SIDEBAR_OPENED_COMPONENT_CACHE:
    return {
      ...state,
      sidebarOpenedComponentCache: {
        ...state.sidebarOpenedComponentCache,
        accordions: {
          ...state.sidebarOpenedComponentCache.accordions,
          ...get(action.openedComponentCache, 'accordions', {}),
          tiles: {
            ...state.sidebarOpenedComponentCache.accordions.tiles,
            ...get(action.openedComponentCache, 'accordions.tiles', {})
          },
          sections: {
            ...state.sidebarOpenedComponentCache.accordions.sections,
            ...get(action.openedComponentCache, 'accordions.sections', {})
          },
        },
      }
    };

  case END_ALL_EDITING:
    return {
      ...state,
      currentlyEditingSectionId: null,
      currentlyEditingContentUuids: []
    };

  case SET_EDITING_TILE_CAPTION:
    return {
      ...state,
      currentlyEditingTileCaptionUuid: action.contentUuid
    };

  case ADD_SECTION:
    return {
      ...state,
      currentlyEditingSectionId:  action.shouldEditSection && !action.tab ? action.section.id : state.currentlyEditingSectionId,
      sectionOverridesBySectionId: {
        ...state.sectionOverridesBySectionId,
        [action.section.id]: {
          ...state.sectionOverridesBySectionId[action.section.id],
          ...action.sectionOverrides
        }
      }
    };

  case 'SET_SECTION_CREATOR_SECTION':
    return {
      ...state,
      sectionCreatorSectionData: action.data
    };

  case SET_IS_HEADER_EDITOR_OPEN:
    return {
      ...state,
      isHeaderEditorOpen: action.isOpen
    };

  case SET_IS_THEME_SELECTOR_OPEN:
    return {
      ...state,
      isThemeSelectorOpen: action.isOpen
    };

  case SET_IS_STYLE_SELECTOR_OPEN:
    return {
      ...state,
      isStyleSelectorOpen: action.isOpen
    };

  case SET_IS_TRANSLATIONS_EDITOR_OPEN:
    return {
      ...state,
      isTranslationsEditorOpen: action.isOpen
    };

  case SET_IS_PAGE_SCOPED_CSS_EDITOR_OPEN:
    return {
      ...state,
      isPageScopedCssEditorOpen: action.isOpen
    };

  case 'SET_CURRENT_EDITOR_MODAL':
    return {
      ...state,
      currentModalName: action.name,
      currentModalProps: action.props,
      currentModalIdent: action.ident
    };

  case 'SET_SIDEBAR_IS_EXPANDED':
    return {
      ...state,
      isSidebarExpanded: action.isExpanded
    };

  case SET_SIDEBAR_EXPANSION_SIZE:
    const { sidebarExpansionSize } = action;
    return {
      ...state,
      sidebarExpansionSize
    };

  case QUEUE_OVERLAY_CONTENT_ITEM_FOR_DELETE:
    return {
      ...state,
      overlayContentItemsToDelete: uniq([
        ...state.overlayContentItemsToDelete || [],
        action.contentItem
      ])
    };

  case QUEUE_DOWNLOADABLE_CONTENT_ITEM_FOR_DELETE:
    return {
      ...state,
      downloadableContentItemsToDelete: uniq([
        ...state.downloadableContentItemsToDelete || [],
        action.contentItem
      ])
    };

  case SET_SHOULD_SHOW_GRID_LINES:
    return {
      ...state,
      shouldShowGridLines: action.shouldShowGridLines
    };

  case SET_IS_EDITING_TILE_TAXONOMY:
    return {
      ...state,
      isEditingTileTaxonomy: action.isEditingTileTaxonomy
    };
  default:
    return state;

  }
};

export const getDebugBar = (state)=>
  !!state.debugBar;

export const getCurrentFroalaLinkEditorInstance = (state)=>
  state.currentFroalaLinkEditorInstance;

export const getEditorOnlySectionOverrides = (state, sectionOrId)=>
  state.sectionOverridesBySectionId[getIdFrom(sectionOrId)];

export const getEditorOnlySectionOverridesBySectionId = (state) =>
  state.sectionOverridesBySectionId;

export const getIsChoosingTarget = (state)=>
  !!state.isChoosingTarget;

export const getIsChoosingSection = (state)=>
  !!state.isChoosingTarget && !state.chosenContentUuid;

export const getIsChoosingContentItem = (state)=>
  !!state.isChoosingTarget && !!state.chosenContentUuid;

export const getIsEditingTileTaxonomy = (state) =>
  !!state.isEditingTileTaxonomy;

export const getChosenSectionId = (state)=>
  state.chosenSectionId;

export const getChosenContentUuid = (state)=>
  state.chosenContentUuid;

export const getCurrentlyEditingTileCaptionUuid = (state)=>
  state.currentlyEditingTileCaptionUuid;

export const getCurrentlyEditingSectionId = (state)=>
  state.currentlyEditingSectionId;

export const getCurrentlyEditingContentUuids = (state)=>
  state.currentlyEditingContentUuids;

export const getCurrentlyEditingSection = (state)=>
  state.currentlyEditingSection;

export const getActiveTabIdBySectionId = (state)=>
  state.activeTabIdBySectionId || {};

export const getHasPastedAtLeastOnceInFroala = (state)=>
  !!state.hasPastedAtLeastOnceInFroala;

export const getHasHiddenFroalaPasteNotification = (state)=>
  !!state.hasHiddenFroalaPasteNotification;

export const getShouldShowFroalaPasteNotification = (state)=> {
  const hasPastedAtLeastOnceInFroala = getHasPastedAtLeastOnceInFroala(state);
  const hasHiddenFroalaPasteNotification = getHasHiddenFroalaPasteNotification(state);
  const hasDimissedFroalaPasteNotificationForever = !!get(getUserStore().get('user_onboarding_data'), 'has_dismissed_froala_paste_notification');

  return (
    !hasDimissedFroalaPasteNotificationForever &&
    !hasHiddenFroalaPasteNotification &&
    hasPastedAtLeastOnceInFroala
  );
};

export const getGridSectionSingleTileEditorCurrentTab = (state)=>
  state.gridSectionSingleTileEditorCurrentTab || TILE_EDITOR_TAB_STYLING;

export const getSectionEditorCurrentTab = (state)=>
  state.sectionEditorCurrentTab || SECTION_EDITOR_TAB_STYLING;

export const getTemporaryFroalaLinkData = (state)=>
  state.temporaryFroalaLinkData;

export const getTemporaryMenuData = (state)=>
  state.temporaryMenuData;

export const getTemporaryTileActionData = (state)=>
  state.temporaryTileActionData;

export const getEditingOverlayData = (state)=>
  state.editingOverlayData;

export const getIsThemeSelectorOpen = (state)=>
  state.isThemeSelectorOpen;

export const getIsStyleSelectorOpen = (state)=>
  state.isStyleSelectorOpen;

export const getIsTranslationsEditorOpen = (state)=>
  state.isTranslationsEditorOpen;

export const getIsPageScopedCssEditorOpen = (state)=>
  state.isPageScopedCssEditorOpen;

export const getIsHeaderEditorOpen = (state)=>
  state.isHeaderEditorOpen;

export const getCurrentModalName = (state)=>
  state.currentModalName;

export const getCurrentModalProps = (state)=>
  state.currentModalProps || {};

export const getCurrentModalIdent = (state)=>
  state.currentModalIdent;

export const getCanPasteTileData = (state)=>
  !isEmpty(getClipboardItemsByType(state, CLIPBOARD_ITEM_TYPE_TILE));

export const getCanPasteSectionData = (state)=>
  !isEmpty(getClipboardItemsByType(state, CLIPBOARD_ITEM_TYPE_SECTION));

export const getCanPasteSubpageData = (state)=>
  !isEmpty(getClipboardItemsByType(state, CLIPBOARD_ITEM_TYPE_SUBPAGE));

export const getClipboardItemsByType = (state, type) => {
  return filter(state.clipboardItems, item => item.type === type);
};

export const getTemporaryPageTitle = (state)=>
  state.temporaryPageTitle;

export const getIsOpenPageTitleEditor = (state)=>
  state.isOpenPageTitleEditor;

export const getSectionCreatorSectionData = (state)=>
  state.sectionCreatorSectionData || {};

export const getIsSidebarExpanded = (state)=>
  !!state.isSidebarExpanded;

export const getIsPageScopedCssValid = (state)=>
  !!state.isPageScopedCssValid;

export const getOverlayContentItemsToDelete  = (state)=>
  state.overlayContentItemsToDelete || [];

export const getDownloadableContentItemsToDelete  = (state)=>
  state.downloadaleContentItemsToDelete || [];

export const getShouldShowGridLines = (state) =>
  !!state.shouldShowGridLines;

export const getSidebarExpansionSize = (state) =>
  getIsPageScopedCssEditorOpen(state) ?
    state.sidebarExpansionSize
  :
    0;

export const getSidebarOpenedComponentCache = (state) =>
  state.sidebarOpenedComponentCache;
